import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  List,
  ListItem,
  Stack,
  Switch,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { NumberInput } from "@components/ui-kit";
import { NumberInputPercent } from "@components/ui-kit/number-input-percent";
import { Trans, t } from "@lingui/macro";
import type { BudgetCategory } from "@src/__generated__/urql-graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { ModalInvoices } from "@src/components/widgets/Modals/ModalInvoices";
import { currency, nominate } from "@src/utils/formatters";
import {
  fieldNumberModelToInputProps,
  fieldToCheckboxProps,
} from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { NumberModel } from "@src/utils/models";
import { secondsToHours } from "@src/utils/time";
import { sumBy } from "lodash";
import { observer } from "mobx-react-lite";
import React, { type FunctionComponent } from "react";
import type { TOurWorkBudgetItem } from "../../../../models";
import { useInvoiceStore } from "../../../../store";
import { AlreadyInvoiced } from "./AlreadyInvoiced";
import { ClosedItemWarning } from "./ClosedItemWarning";

interface OurWorkItemProps {
  item: TOurWorkBudgetItem;
  disabled: boolean;
  budgetCategoryId: BudgetCategory["id"];
}

const POSITION_TEMPLATE_COLUMN = "1fr 0.375fr 0.25fr 0.375fr";

export const OurWorkItem: FunctionComponent<OurWorkItemProps> = observer(
  function OurWorkItem({ item, disabled, budgetCategoryId }) {
    const { workspaceStore } = useStore();
    const store = useInvoiceStore();

    const modalInvoices = useDisclosure();

    const budgetSplitForm = store.form.our_work_budget_splits.$.get(item.id);
    const isChecked = !!budgetSplitForm;

    const updateTotalAmount = () => {
      if (!budgetSplitForm) return;
      const totalAmount = sumBy(
        Array.from(budgetSplitForm.$.invoiced_positions.$),
        ([_, form]) => {
          return form.$.amount.value.denominatedValue;
        },
      );
      budgetSplitForm.$.amount.onChange(new NumberModel(totalAmount, 2));
    };

    return (
      <Stack
        px="5"
        py="6"
        bg={isChecked ? "blue.50" : "transparent"}
        borderWidth="1px"
        borderColor={isChecked ? "blue.400" : "transparent"}
        rounded="xl"
      >
        <Grid gap="2" templateColumns={{ base: "1fr", md: "1fr 0.5fr" }}>
          <HStack align="start">
            <Box mr="2" pt="1">
              <Checkbox
                id={`budget-item-${item.id}`}
                isChecked={isChecked}
                isDisabled={disabled}
                onChange={(e) =>
                  store.toggleCheckOurWorkItem(
                    item,
                    e.target.checked,
                    budgetCategoryId,
                  )
                }
              />
            </Box>
            <Stack dir="column">
              {!!item.completed_at && <ClosedItemWarning />}

              <FormLabel
                mb="3"
                fontSize="md"
                fontWeight="semibold"
                htmlFor={`budget-item-${item.id}`}
              >
                {item.title} (
                {currency.formatByWorkspace(item.stats.total_budget)})
              </FormLabel>
              <Button
                justifyContent="start"
                onClick={modalInvoices.onOpen}
                variant="link"
              >
                <Trans>Invoiced:</Trans>{" "}
                {currency.formatByWorkspace(item.stats.credit)}
              </Button>
              <ModalInvoices
                {...modalInvoices}
                ourWorkBudgetItemId={item.id}
                onTransfer={store.selectedProjectsRefetch}
              />
              <Text mb="1" color="grey.600">
                <Trans>Uninvoiced:</Trans>{" "}
                {currency.formatByWorkspace(item.stats.uninvoiced)}
              </Text>
            </Stack>
          </HStack>
          <Stack pl={{ base: "24px", md: "unset" }} dir="column">
            {isChecked &&
              (budgetSplitForm ? (
                <React.Fragment>
                  <label
                    className="text-2xl text-gray-700 mb-2"
                    htmlFor="amount"
                  >
                    <Trans>Amount</Trans>
                  </label>
                  <NumberInputPercent
                    currency
                    placeholder={t`Amount`}
                    disabled={
                      disabled || budgetSplitForm.$.invoice_from_positions.value
                    }
                    suffix={workspaceStore.settings!.currency.code}
                    {...fieldNumberModelToInputProps(budgetSplitForm.$.amount)}
                    onChange={(value) =>
                      NumberModel.setFieldState(budgetSplitForm.$.amount, value)
                    }
                    percentProps={{
                      ...fieldNumberModelToInputProps(
                        budgetSplitForm.$.amount_in_percent,
                      ),
                      isDisabled:
                        !store.isRFI && store.isEdit && item.stats.credit > 0,
                      disabledTooltip: t`Percentage editing is disabled for existing invoices. The uninvoiced amount may have changed since this invoice was created.`,
                      onChange: (value: string) =>
                        NumberModel.setFieldState(
                          budgetSplitForm.$.amount_in_percent,
                          value,
                        ),
                      availableValue: item.stats.uninvoiced,
                    }}
                  />
                </React.Fragment>
              ) : (
                <AlreadyInvoiced />
              ))}
          </Stack>
        </Grid>

        {budgetSplitForm && budgetSplitForm.$.invoiced_positions.$.size > 0 && (
          <Stack py="3" pl="7" spacing="3">
            <Grid
              alignItems="center"
              gap="2"
              templateColumns={POSITION_TEMPLATE_COLUMN}
              fontSize="xs"
              fontWeight="bold"
            >
              <GridItem>
                <FormControl alignItems="center" flex="1" display="flex">
                  <FormLabel mb="0" htmlFor={`${item.id}-show-positions`}>
                    <Trans>Invoice positions</Trans>
                  </FormLabel>
                  <Switch
                    id={`${item.id}-show-positions`}
                    isChecked={budgetSplitForm.$.invoice_from_positions.value}
                    onChange={(e) => {
                      budgetSplitForm.$.invoice_from_positions.onChange(
                        e.target.checked,
                      );
                      budgetSplitForm.$.amount.onChange(new NumberModel(0, 2));
                    }}
                  />
                </FormControl>
              </GridItem>
              {budgetSplitForm.$.invoice_from_positions.value && (
                <>
                  <GridItem>
                    <Trans>Unit price</Trans>
                  </GridItem>
                  <GridItem>
                    <Trans>Quantity</Trans>
                  </GridItem>
                  <GridItem>
                    <Trans>Amount</Trans>
                  </GridItem>
                </>
              )}
            </Grid>

            {budgetSplitForm.$.invoice_from_positions.value && (
              <Stack spacing="4">
                {Array.from(budgetSplitForm.$.invoiced_positions.$).map(
                  ([position, form]) => (
                    <FormControl key={position.time_tracking_work_type_id}>
                      <Grid
                        gap="2"
                        templateColumns={{
                          base: "1fr",
                          md: POSITION_TEMPLATE_COLUMN,
                        }}
                      >
                        <GridItem>
                          <HStack align="start" spacing="3">
                            <Box pt="1">
                              <Checkbox
                                id={`${item.id}-${position.time_tracking_work_type_id}`}
                                isDisabled={disabled}
                                {...fieldToCheckboxProps(form.$.isInvoiced)}
                                onChange={({ target }) => {
                                  form.$.isInvoiced.onChange(target.checked);
                                  const nominatedUninvoicedAmount =
                                    position.uninvoiced_amount
                                      ? nominate(
                                          position.uninvoiced_amount,
                                          workspaceStore.settings?.currency
                                            .denomination ?? 2,
                                        )
                                      : 0;

                                  NumberModel.setFieldState(
                                    form.$.amount,
                                    target.checked
                                      ? nominatedUninvoicedAmount
                                      : 0,
                                  );
                                  form.$.quantity.onChange(
                                    target.checked
                                      ? (
                                          (position.uninvoiced_time ?? 3600) /
                                          3600
                                        ).toString()
                                      : "1",
                                  );
                                  const unitAmount =
                                    form.$.amount.value.value /
                                    Number(form.$.quantity.value);
                                  NumberModel.setFieldState(
                                    form.$.unit_amount,
                                    target.checked
                                      ? Number.isNaN(unitAmount)
                                        ? 0
                                        : unitAmount
                                      : 0,
                                  );
                                  updateTotalAmount();
                                }}
                              />
                            </Box>
                            <Stack flex="1">
                              <Box>
                                <FormLabel
                                  display="inline"
                                  w="auto"
                                  mb="0"
                                  fontWeight="semibold"
                                  htmlFor={`${item.id}-${position.time_tracking_work_type_id}`}
                                >
                                  {position.title}
                                </FormLabel>
                              </Box>
                              <HStack>
                                <Tooltip
                                  label={
                                    <List>
                                      <ListItem>
                                        <Trans>Budgeted:</Trans>
                                        {` ${currency.formatByWorkspace(position.budgeted_amount || 0)} 
                                (${workspaceStore.settings?.timeTrackingRateValueReadable(
                                  secondsToHours(position.budgeted_time || 0),
                                )})`}
                                      </ListItem>
                                      <ListItem>
                                        <Trans>Invoiced:</Trans>
                                        {` ${currency.formatByWorkspace(
                                          position.total_invoiced_amount || 0,
                                        )} 
                                (${workspaceStore.settings?.timeTrackingRateValueReadable(
                                  secondsToHours(position.total_invoiced_time),
                                )})`}
                                      </ListItem>
                                      <ListItem>
                                        <Trans>Uninvoiced:</Trans>
                                        {` ${currency.formatByWorkspace(position.uninvoiced_amount || 0)} 
                                (${workspaceStore.settings?.timeTrackingRateValueReadable(
                                  secondsToHours(position.uninvoiced_time),
                                )})`}
                                      </ListItem>
                                      <ListItem>
                                        <Trans>Tracked:</Trans>
                                        {` ${currency.formatByWorkspace(position.tracked_amount ?? 0)} `}
                                        (
                                        {workspaceStore.settings?.timeTrackingRateValueReadable(
                                          secondsToHours(position.tracked_time),
                                        )}
                                        )
                                      </ListItem>
                                    </List>
                                  }
                                >
                                  <Text color="grey.600">
                                    <Trans>Uninvoiced:</Trans>
                                    {` ${currency.formatByWorkspace(position.uninvoiced_amount || 0)}  
                            (${workspaceStore.settings?.timeTrackingRateValueReadable(
                              (position.uninvoiced_time || 0) / 3600,
                            )})`}
                                    <Icon name="help-circle" ml="2" />
                                  </Text>
                                </Tooltip>
                              </HStack>
                            </Stack>
                          </HStack>
                        </GridItem>

                        {form.$.isInvoiced.value && (
                          <>
                            <GridItem>
                              <NumberInput
                                currency
                                placeholder={t`Unit price`}
                                disabled={disabled}
                                suffix={workspaceStore.settings!.currency.code}
                                {...fieldNumberModelToInputProps(
                                  form.$.unit_amount,
                                  (value) => {
                                    NumberModel.setFieldState(
                                      form.$.amount,
                                      Number(value) * Number(form.$.quantity.$),
                                    );
                                    updateTotalAmount();
                                  },
                                )}
                                error={
                                  store.form.invoicePositionAmountRule(
                                    form.$.unit_amount.value,
                                  ) ?? undefined
                                }
                              />
                            </GridItem>
                            <GridItem>
                              <NumberInput
                                currency
                                placeholder={t`Quantity`}
                                disabled={disabled}
                                suffix={t`Hrs`}
                                value={form.$.quantity.value}
                                onChange={(value) => {
                                  form.$.quantity.onChange(value);
                                  NumberModel.setFieldState(
                                    form.$.amount,
                                    Number(value) *
                                      Number(form.$.unit_amount.$.value),
                                  );
                                  updateTotalAmount();
                                }}
                                error={form.$.quantity.error ?? undefined}
                              />
                            </GridItem>
                            <GridItem>
                              <NumberInput
                                currency
                                placeholder={t`Amount`}
                                disabled={disabled}
                                suffix={workspaceStore.settings!.currency.code}
                                {...fieldNumberModelToInputProps(
                                  form.$.amount,
                                  (value) => {
                                    NumberModel.setFieldState(
                                      form.$.unit_amount,
                                      Number(value) / Number(form.$.quantity.$),
                                    );

                                    updateTotalAmount();
                                  },
                                )}
                                error={
                                  store.form.invoicePositionAmountRule(
                                    form.$.amount.value,
                                  ) ?? undefined
                                }
                              />
                            </GridItem>
                          </>
                        )}
                      </Grid>
                    </FormControl>
                  ),
                )}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    );
  },
);
